import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { AppSidebar } from '@/components/features/AppSidebar';
import { GlobalConnieChat } from '@/components/features/GlobalConnieChat';
import { Header } from '@/components/features/Header';
import { PageState } from '@/components/features/PageState';
import { useAuth, useGetCompanyRegistration, useLogout } from '@/store/auth';
import { useCompany } from '@/store/company';

import { ROUTES } from './constant';
import { SidebarLayout } from './layouts/SidebarLayout';
import { getDocumentTitle } from './utils/pageTitle';
import storage from './utils/storage';

const RECONNECT_LOADER_DELAY_MS = 1500;

export default function AppLayout() {
  const { user, isLoading } = useAuth();
  const logout = useLogout();
  const location = useLocation();
  const isCompanyRootRoute = location.pathname.startsWith(ROUTES.COMPANY_ROOT);
  const isEmailVerificationRoute = location.pathname === '/signup/verify';
  const {
    data: company,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
  } = useCompany();
  const { isLoading: isCompanyRegistrationLoading, data: companyRegistration } =
    useGetCompanyRegistration(Boolean(user && isCompanyRootRoute));
  const shouldForceRelogin =
    location.pathname === ROUTES.COMPANY_PENDING &&
    companyRegistration?.status.toLowerCase() === 'approved' &&
    !!user;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isReconnectVisible, setIsReconnectVisible] = useState(false);
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    if (shouldForceRelogin) {
      logout();
    }
  }, [shouldForceRelogin, logout]);

  useEffect(() => {
    storage.setItem('chakra-ui-color-mode', 'dark');
  }, []);

  useEffect(() => {
    document.title = getDocumentTitle(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoading) {
      setIsReconnectVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsReconnectVisible(true);
    }, RECONNECT_LOADER_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading]);

  if (isLoading && isReconnectVisible) {
    return <PageState variant='reconnecting' />;
  }
  if (isLoading) return null;

  if (isMaintenanceMode) {
    return <PageState variant='maintenance' />;
  }

  if (user && isCompanyRootRoute && isCompanyRegistrationLoading) {
    return <PageState variant='reconnecting' />;
  }

  if (shouldForceRelogin) {
    return <Navigate to='/signin' replace />;
  }

  if (!!user && !user.company_id) {
    if (isCompanyRootRoute || isEmailVerificationRoute) {
      return <Outlet />;
    }

    return <Navigate to={ROUTES.COMPANY_CREATE} replace />;
  }

  if (user?.company_id && isCompanyLoading) {
    return null;
  }

  if (user?.company_id && isCompanyError) {
    return <PageState variant='error' />;
  }

  if (
    user?.company_id &&
    !isCompanyLoading &&
    company &&
    !company.bc_client_id
  ) {
    if (isCompanyRootRoute) {
      return <Outlet />;
    }

    return <Navigate to={ROUTES.COMPANY_BC_SETUP} replace />;
  }

  // TODO: This whole layout needs to be refactored
  // If this is the signin, signup or forgot password route, render it without authentication check
  if (
    location.pathname === '/signin' ||
    location.pathname === ROUTES.INVITE_ACCEPT ||
    (location.pathname.startsWith('/signup') &&
      !location.pathname.startsWith(ROUTES.COMPANY_ROOT)) ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password'
  ) {
    if (isEmailVerificationRoute) {
      return <Outlet />;
    }
    if (!user) {
      return <Outlet />;
    }
    return <Navigate to='/' replace />;
  }

  // For all other routes, require authentication
  if (!user) {
    return <Navigate to='/signin' replace />;
  }

  return (
    <SidebarLayout
      sidebar={
        <AppSidebar
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
      }
      navbar={
        <Header
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
      }
    >
      <Outlet />
      <GlobalConnieChat />
    </SidebarLayout>
  );
}
